import { SCALE_CHORD_QUALITIES } from '../../../utils/music/constants';
import { ChordQuality } from '../../../types';

export class HarmonicColor {
    /**
     * DISSONANCE REDUCTION (Harmonic Intelligence)
     * Returns a degree and quality, potentially substituting dissonant chords.
     */
    public getSafeChordDegree(degree: number, scaleType: string, allowDissonanceProb: number = 0.05): { degree: number, quality: ChordQuality } {
        const chordQualities = SCALE_CHORD_QUALITIES[scaleType];
        if (!chordQualities) return { degree, quality: 'major' }; // Fallback

        const safeDegree = degree % chordQualities.length; // Safety
        let quality = chordQualities[safeDegree];

        // Verify if quality is dissonant (diminished/augmented)
        const isDissonant = quality === 'diminished' || quality === 'augmented';

        if (isDissonant) {
            const allowDissonance = Math.random() < allowDissonanceProb;

            if (!allowDissonance) {
                // SUBSTITUTE: Pick a neighbor (degree -1 or +1)
                const shift = Math.random() < 0.5 ? -1 : 1;
                let newDegree = degree + shift;

                // Wrap around scale logic
                if (newDegree < 0) newDegree = 6;

                // Re-calculate quality
                const newQuality = chordQualities[newDegree % chordQualities.length];

                // Check if substitute is ALSO dissonant
                if (newQuality === 'diminished' || newQuality === 'augmented') {
                    newDegree = degree + (shift * -1);
                }

                return { degree: newDegree, quality: chordQualities[newDegree % chordQualities.length] };
            }
        }

        return { degree: safeDegree, quality };
    }

    /**
     * FEATURE 5: Modal Interchange (Emotional Color)
     * Randomly swaps the current chord for a parallel mode variant.
     * e.g. IV Major -> iv Minor (Nostalgia).
     */
    public getBorrowedChord(
        degree: number,
        scaleType: string,
        rootMidi: number,
        force: boolean = false
    ): { isBorrowed: boolean, quality?: ChordQuality, rootMidi?: number } {
        // Only borrow occasionally (12%), unless forced
        if (!force && Math.random() > 0.12) return { isBorrowed: false };

        // 1. Major Scale Interchanges
        if (scaleType === 'major' || scaleType === 'pentatonic_major') {
            // I = 0, II = 1, III = 2, IV = 3, V = 4, VI = 5, VII = 6

            // BORROW: Minor IV (iv) - The "Nostalgia" Chord
            if (degree === 3) {
                return { isBorrowed: true, quality: 'minor', rootMidi: rootMidi };
            }

            // BORROW: Minor V (v) - Mixolydian feel
            if (degree === 4) {
                return { isBorrowed: true, quality: 'minor', rootMidi: rootMidi };
            }

            // BORROW: bVII (Flat 7) - B -> Bb Major. Backdoor resolution.
            if (degree === 6) {
                return { isBorrowed: true, quality: 'major', rootMidi: rootMidi - 1 };
            }

            // BORROW: bVI (Flat 6) - A -> Ab Major. Epic/Cinematic.
            if (degree === 5) {
                // Root is normally A (9 semitones from C). bVI is Ab (8 semitones).
                return { isBorrowed: true, quality: 'major', rootMidi: rootMidi - 1 };
            }

            // BORROW: bIII (Flat 3) - E -> Eb Major. Heroic/Dark.
            if (degree === 2) {
                // Root is normally E (4 semitones). bIII is Eb (3 semitones).
                return { isBorrowed: true, quality: 'major', rootMidi: rootMidi - 1 };
            }
        }

        // 2. Minor Scale Interchanges
        if (scaleType === 'minor' || scaleType === 'pentatonic_minor') {
            // Picardy Third: End on Major I
            if (degree === 0 && (force || Math.random() < 0.2)) {
                return { isBorrowed: true, quality: 'major', rootMidi: rootMidi };
            }

            // Dorian IV: iv -> IV (Major). Fantasy feel.
            if (degree === 3) {
                return { isBorrowed: true, quality: 'major', rootMidi: rootMidi };
            }
        }

        return { isBorrowed: false };
    }

    /**
     * V9: TENSION/RELEASE - Modify chord quality based on tension phase.
     * During tension: More suspensions (sus4/sus2) for unresolved feeling
     * During release: Force resolution to major/minor
     */
    public getTensionChordModification(
        quality: ChordQuality,
        tensionPhase: 'tension' | 'release' | 'neutral'
    ): ChordQuality {
        if (tensionPhase === 'tension') {
            // 60% chance to convert to suspension during tension buildup
            if (Math.random() < 0.6) {
                return Math.random() < 0.5 ? 'sus4' : 'sus2';
            }
        }

        if (tensionPhase === 'release') {
            // Force resolution: convert suspensions back to stable chords
            if (quality === 'sus4' || quality === 'sus2') {
                // Resolve sus4 -> major (typically), sus2 -> minor (for variety)
                return quality === 'sus4' ? 'major' : 'minor';
            }
        }

        return quality;
    }

    /**
     * V5: FUNCTIONAL HARMONY (Secondary Dominants)
     * Returns a Dominant 7th chord that resolves to the *next* target degree.
     * e.g. If target is V (G), return V/V (D7).
     */
    public getSecondaryDominant(
        targetDegree: number,
        scaleType: string,
        rootMidi: number
    ): { notes: number[], root: number } | null {
        // Only Secondary Dominants for Major/Minor scales for now
        if (scaleType !== 'major' && scaleType !== 'minor') return null;

        // V/X = Dominant 7th built on the 5th of the Target

        // Target Root relative to Key Root (Major Scale Intervals)
        // 0  2  4  5  7  9  11
        const majorIntervals = [0, 2, 4, 5, 7, 9, 11];
        const minorIntervals = [0, 2, 3, 5, 7, 8, 10];

        const intervals = scaleType === 'major' ? majorIntervals : minorIntervals;
        const targetInterval = intervals[targetDegree % 7];

        const targetRootMidi = rootMidi + targetInterval;

        // 2. Find the V of the Target (Perfect 5th above Target)
        const secondaryDominantRoot = targetRootMidi + 7;

        // 3. Build a Dominant 7th (M3, P5, m7) on that root
        // R + 4, R + 7, R + 10
        const dom7Intervals = [0, 4, 7, 10];
        const notes = dom7Intervals.map(i => secondaryDominantRoot + i);

        // Normalize octaves (keep within reasonable range)
        const normalized = notes.map(n => {
            while (n > 80) n -= 12;
            return n;
        });

        return { notes: normalized, root: secondaryDominantRoot };
    }
}
